import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui-kit/avatar';
import { Button } from '@/components/ui-kit/button';
import { Card } from '@/components/ui-kit/card';
import { Input } from '@/components/ui-kit/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui-kit/tooltip';

import { Search, ShieldCheck, Trash2, UserCog, UserPlus, Users } from 'lucide-react';

import { UserRole } from '@/state/store/auth/index.type';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { SerializedUser } from './users-admin-client';

export type RoleFilter = 'all' | 'sys_admin' | 'sys_editor' | 'none';

interface UsersListProps {
  users: SerializedUser[];
  roleFilter: RoleFilter;
  onRoleChange: (userId: string, newRole: UserRole, currentRole: UserRole | null) => void;
  onDelete: (user: SerializedUser) => void;
  onInvite: () => void;
  /** Right-side slot for the search toolbar — page passes the refresh
      button here so it sits next to the search box on mobile. */
  searchToolbarAction?: React.ReactNode;
}

export function UsersList({
  users,
  roleFilter,
  onRoleChange,
  onDelete,
  onInvite,
  searchToolbarAction,
}: UsersListProps) {
  const [search, setSearch] = useState('');
  const { t } = useTranslation();

  // Filter by search
  const searchFilteredUsers = users.filter(
    (user) =>
      user.email.toLowerCase().includes(search.toLowerCase()) ||
      (user.firstName?.toLowerCase() || '').includes(search.toLowerCase()) ||
      (user.lastName?.toLowerCase() || '').includes(search.toLowerCase())
  );

  // Filter by role
  const filteredUsers = searchFilteredUsers.filter((user) => {
    if (roleFilter === 'all') return true;
    if (roleFilter === 'sys_admin') return user.role === 'sys_admin';
    if (roleFilter === 'sys_editor') return user.role === 'sys_editor';
    if (roleFilter === 'none') return !user.role;
    return true;
  });

  const formatLastActive = (timestamp: number | null) => {
    if (!timestamp) return t('ADMIN_USERS_LIST_LASTACTIVE_NEVER');
    const date = new Date(timestamp).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
    return t('ADMIN_USERS_LIST_LASTACTIVE_ACTIVE', { date });
  };

  const getUserName = (user: SerializedUser) => {
    if (user.firstName || user.lastName) {
      return `${user.firstName || ''} ${user.lastName || ''}`.trim();
    }
    return null;
  };

  const getInitials = (user: SerializedUser) => {
    const name = getUserName(user);
    if (name) {
      const parts = name.split(' ');
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
      }
      return name[0].toUpperCase();
    }
    return user.email[0].toUpperCase();
  };

  const getRoleValue = (role: UserRole | null): string => {
    return role || 'none';
  };

  const handleRoleSelectChange = (userId: string, value: string, currentRole: UserRole | null) => {
    const newRole = value as UserRole;
    if (newRole !== currentRole) {
      onRoleChange(userId, newRole, currentRole);
    }
  };

  // Empty state component
  if (users.length === 0) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 md:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-globe-grey" />
            <Input
              placeholder={t('ADMIN_USERS_LIST_SEARCHPLACEHOLDER')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-10! shadow-none"
            />
          </div>
          {searchToolbarAction}
        </div>
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-selise-blue/10 mb-4">
              <Users className="size-8 text-selise-blue" />
            </div>
            <h3 className="text-lg font-semibold text-[hsl(var(--fg))] mb-2">
              {t('ADMIN_USERS_LIST_EMPTYSTATE_TITLE')}
            </h3>
            <p className="text-sm text-globe-grey mb-6 max-w-sm">
              {t('ADMIN_USERS_LIST_EMPTYSTATE_DESCRIPTION')}
            </p>
            <Button onClick={onInvite}>
              <UserPlus className="size-4" />
              {t('ADMIN_USERS_LIST_EMPTYSTATE_INVITEACTION')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Search row — refresh button slides in via searchToolbarAction. */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 md:max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-globe-grey" />
          <Input
            placeholder={t('ADMIN_USERS_LIST_SEARCHPLACEHOLDER')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
        {searchToolbarAction}
      </div>

      {/* Users Grid */}
      {filteredUsers.length === 0 ? (
        <Card className="p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="flex size-16 items-center justify-center rounded-full bg-[hsl(var(--globe-grey))]/10 mb-4">
              <Search className="size-8 text-globe-grey" />
            </div>
            <h3 className="text-lg font-semibold text-[hsl(var(--fg))] mb-2">
              {t('ADMIN_USERS_LIST_EMPTYRESULTS_TITLE')}
            </h3>
            <p className="text-sm text-globe-grey max-w-sm">
              {search
                ? t('ADMIN_USERS_LIST_EMPTYRESULTS_WITHSEARCH', {
                    query: search,
                    filter: roleFilter === 'all' ? 'all' : 'filtered',
                  })
                : t('ADMIN_USERS_LIST_EMPTYRESULTS_NOSEARCH')}
            </p>
          </div>
        </Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredUsers.map((user) => {
            const name = getUserName(user);
            const initials = getInitials(user);

            return (
              <Card key={user.id} className="p-4 hover:shadow-md transition-shadow">
                <div className="flex flex-col h-full">
                  {/* User Info */}
                  <div className="flex items-start gap-3 mb-4">
                    <Avatar className="size-12">
                      <AvatarImage src={user.imageUrl || undefined} alt={name || user.email} />
                      <AvatarFallback className="bg-selise-blue/10 text-selise-blue font-medium">
                        {initials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      {name && (
                        <p className="font-semibold text-[hsl(var(--fg))] truncate">{name}</p>
                      )}
                      <p
                        className={`text-sm truncate ${
                          name ? 'text-globe-grey' : 'font-semibold text-[hsl(var(--fg))]'
                        }`}
                      >
                        {user.email}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 mt-auto">
                    <Select
                      value={getRoleValue(user.role)}
                      onValueChange={(value) => handleRoleSelectChange(user.id, value, user.role)}
                    >
                      <SelectTrigger className="flex-1 h-9 border-secondary-200! text-black!">
                        <SelectValue className="text-black!" />
                      </SelectTrigger>
                      <SelectContent className="border-secondary-200!">
                        <SelectItem value="sys_admin">
                          <div className="flex items-center gap-2">
                            <ShieldCheck className="size-4 text-selise-blue" />
                            <span>{t('ADMIN_USERS_ROLES_ADMIN')}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="sys_editor">
                          <div className="flex items-center gap-2">
                            <UserCog className="size-4 text-lime-green" />
                            <span>{t('ADMIN_USERS_ROLES_EDITOR')}</span>
                          </div>
                        </SelectItem>
                        <SelectItem value="none">
                          <div className="flex items-center gap-2">
                            <Users className="size-4 text-globe-grey" />
                            <span>{t('ADMIN_USERS_ROLES_NONE')}</span>
                          </div>
                        </SelectItem>
                      </SelectContent>
                    </Select>

                    <TooltipProvider>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="shrink-0 text-globe-grey hover:text-destructive hover:bg-destructive/10"
                            onClick={() => onDelete(user)}
                          >
                            <Trash2 className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>{t('ADMIN_USERS_LIST_ACTIONS_DELETE')}</TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  </div>

                  {/* Last Active */}
                  <p className="text-xs text-globe-grey mt-3">
                    {formatLastActive(user.lastSignInAt)}
                  </p>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
